import { Head } from "@inertiajs/react"
import { Card, Flex, Heading, Text } from "@radix-ui/themes"
import { Edit } from "lucide-react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import { MarkdownPreview } from "@/components/markdown-preview"
import ProjectLayout from "@/layouts/project-layout"
import { editProjectEmailPath, projectDashboardPath, projectEmailsPath } from "@/routes"
import { EmailSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  email: EmailSchema,
  email_body_html: z.string().optional(),
  selected_email_style: z
    .object({
      value: z.string(),
      css: z.string(),
    })
    .optional(),
})

type Props = z.infer<typeof PropsSchema>

export default function EmailShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, email, selected_email_style } = validatedProps

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Email Templates", href: projectEmailsPath(project.slug) },
        { title: email.subject ?? "Untitled" },
      ]}
    >
      <Head title={email.subject ?? "Email Template"} />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            {email.subject ?? "Untitled Email Template"}
          </Heading>

          <LinkButton href={editProjectEmailPath(project.slug, email.id)} variant="classic">
            <Edit className="mr-1 h-4 w-4" />
            Edit
          </LinkButton>
        </Flex>

        <Card>
          <Flex direction="column" gap="4">
            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Custom ID
              </Text>
              <Text as="div" size="2" color="gray">
                <code>{email.custom_id}</code>
              </Text>
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Subject
              </Text>
              <Text as="div" size="2" color="gray">
                {email.subject ?? "No subject"}
              </Text>
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Style
              </Text>
              <Text as="div" size="2" color="gray">
                {selected_email_style?.value ?? "Unknown"}
              </Text>
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Preview
              </Text>
              <MarkdownPreview value={email.body ?? ""} css={selected_email_style?.css ?? ""} />
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Created
              </Text>
              <Text as="div" size="2" color="gray">
                {new Date(email.created_at).toLocaleDateString()}
              </Text>
            </div>
          </Flex>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
